import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Button,
  CircularProgress,
  Divider,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate, useParams } from 'react-router-dom';
import CloseIcon from '@material-ui/icons/Close';
import { SERVER_NAME } from './config';
import { useAuth } from './auth/AuthContext';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    maxWidth: 1200,
    margin: '0 auto',
  },
  card: {
    padding: theme.spacing(2),
    background: '#111',
    color: '#fff',
    marginBottom: theme.spacing(2),
  },
  title: {
    fontWeight: 900,
  },
  subtle: {
    opacity: 0.85,
  },
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    alignItems: 'center',
    marginTop: theme.spacing(1),
  },
  kv: {
    fontFamily: 'monospace',
    fontSize: 12,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    opacity: 0.92,
  },
  error: {
    color: '#ff7777',
    whiteSpace: 'pre-wrap',
    marginTop: theme.spacing(1),
  },
  storyCard: {
    padding: theme.spacing(1.5),
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 10,
    marginTop: theme.spacing(1),
  },
  storyTitle: {
    fontWeight: 800,
  },
  chip: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 999,
    background: 'rgba(255,255,255,0.10)',
    marginRight: 8,
    fontSize: 12,
  },
}));

function safeJsonParse(s) {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

export default function PcapUploadDetailPage() {
  const classes = useStyles();
  const navigate = useNavigate();
  const { uploadId: uploadIdParam } = useParams();
  const uploadId = useMemo(() => String(uploadIdParam || '').trim(), [uploadIdParam]);
  const { isAuthenticated, authChecked } = useAuth();

  const [upload, setUpload] = useState(null);
  const [stories, setStories] = useState([]);
  const [theses, setTheses] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [okMsg, setOkMsg] = useState('');
  const [runningStories, setRunningStories] = useState(false);
  const [runningThesis, setRunningThesis] = useState(false);

  const [openStory, setOpenStory] = useState(null);
  const [openThesis, setOpenThesis] = useState(null);

  const promptLogin = useCallback(() => {
    try {
      if (window && typeof window.globalShowLogin === 'function') {
        window.globalShowLogin(true);
        return;
      }
    } catch (e) {
    }
    navigate('/login');
  }, [navigate]);

  const loadAll = useCallback(async () => {
    if (!uploadId) return;
    if (!isAuthenticated) return;

    setLoading(true);
    setError('');
    setOkMsg('');

    try {
      const [upResp, stResp, thResp] = await Promise.all([
        axios.get(`${SERVER_NAME}/pcap/uploads/${encodeURIComponent(uploadId)}`, { withCredentials: true, timeout: 20000 }),
        axios.get(`${SERVER_NAME}/pcap/uploads/${encodeURIComponent(uploadId)}/stories`, { withCredentials: true, timeout: 20000 }),
        axios.get(`${SERVER_NAME}/pcap/uploads/${encodeURIComponent(uploadId)}/theses`, { withCredentials: true, timeout: 20000 }),
      ]);

      setUpload(upResp && upResp.data ? upResp.data : null);
      setStories(stResp && stResp.data && Array.isArray(stResp.data.items) ? stResp.data.items : []);
      setTheses(thResp && thResp.data && Array.isArray(thResp.data.items) ? thResp.data.items : []);
    } catch (e) {
      const msg = e && e.response && e.response.data && e.response.data.message
        ? String(e.response.data.message)
        : (e && e.message ? String(e.message) : String(e));
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [uploadId, isAuthenticated]);

  const runStories = useCallback(async () => {
    if (!uploadId) return;
    setError('');
    setOkMsg('');
    setRunningStories(true);
    try {
      const resp = await axios.post(`${SERVER_NAME}/pcap/uploads/${encodeURIComponent(uploadId)}/runStories`, null, {
        withCredentials: true,
        timeout: 10 * 60 * 1000,
      });
      const stdout = resp && resp.data && resp.data.stdout ? String(resp.data.stdout) : '';
      setOkMsg(stdout ? `Stories run complete. ${stdout.slice(0, 4000)}` : 'Stories run complete.');
      await loadAll();
    } catch (e) {
      const msg = e && e.response && e.response.data && e.response.data.message
        ? String(e.response.data.message)
        : (e && e.message ? String(e.message) : String(e));
      setError(msg);
    } finally {
      setRunningStories(false);
    }
  }, [uploadId, loadAll]);

  const runThesis = useCallback(async () => {
    if (!uploadId) return;
    setError('');
    setOkMsg('');
    setRunningThesis(true);
    try {
      const resp = await axios.post(`${SERVER_NAME}/pcap/uploads/${encodeURIComponent(uploadId)}/runThesis`, null, {
        withCredentials: true,
        timeout: 10 * 60 * 1000,
      });
      const stdout = resp && resp.data && resp.data.stdout ? String(resp.data.stdout) : '';
      setOkMsg(stdout ? `Thesis run complete. ${stdout.slice(0, 4000)}` : 'Thesis run complete.');
      await loadAll();
    } catch (e) {
      const msg = e && e.response && e.response.data && e.response.data.message
        ? String(e.response.data.message)
        : (e && e.message ? String(e.message) : String(e));
      setError(msg);
    } finally {
      setRunningThesis(false);
    }
  }, [uploadId, loadAll]);

  useEffect(() => {
    if (!authChecked) return;
    if (!isAuthenticated) return;
    loadAll();
  }, [authChecked, isAuthenticated, loadAll]);

  if (!authChecked) {
    return (
      <div className={classes.root}>
        <Paper className={classes.card}>
          <Typography className={classes.title}>PCAP Upload</Typography>
          <Typography className={classes.subtle}>Checking authentication…</Typography>
        </Paper>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className={classes.root}>
        <Paper className={classes.card}>
          <Typography className={classes.title}>PCAP Upload</Typography>
          <Typography className={classes.subtle}>You must be logged in to view upload results.</Typography>
          <div style={{ marginTop: 12 }}>
            <Button variant="contained" color="primary" onClick={promptLogin}>
              Login
            </Button>
          </div>
        </Paper>
      </div>
    );
  }

  const status = upload && upload.status ? String(upload.status) : '';
  const openStoryEvidence = openStory && openStory.evidence_json ? safeJsonParse(String(openStory.evidence_json)) : null;
  const openThesisEvidence = openThesis && openThesis.evidence_json ? safeJsonParse(String(openThesis.evidence_json)) : null;

  return (
    <div className={classes.root}>
      <Paper className={classes.card}>
        <Typography className={classes.title} style={{ marginBottom: 6 }}>
          PCAP Upload Detail
        </Typography>
        <Typography className={classes.subtle}>
          upload_id: <span className={classes.kv}>{uploadId}</span>
        </Typography>

        <Divider style={{ margin: '12px 0', background: 'rgba(255,255,255,0.12)' }} />

        <div className={classes.row}>
          <Button variant="outlined" onClick={() => navigate('/pcap/uploads')}>
            Back to uploads
          </Button>
          <Button variant="outlined" onClick={() => navigate(`/pcap?upload_id=${encodeURIComponent(uploadId)}`)}>
            View in graph
          </Button>
          <Button variant="outlined" onClick={() => navigate('/pcap')}>
            Visualize PCAP
          </Button>
          <Button variant="outlined" onClick={loadAll} disabled={loading}>
            Refresh
          </Button>
          <Button variant="contained" color="secondary" onClick={runStories} disabled={loading || runningStories || runningThesis}>
            {runningStories ? 'Running stories…' : 'Run stories'}
          </Button>
          <Button variant="contained" color="secondary" onClick={runThesis} disabled={loading || runningStories || runningThesis}>
            {runningThesis ? 'Running thesis…' : 'Run thesis'}
          </Button>
          {(loading) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <CircularProgress size={18} style={{ color: '#fff' }} />
              <Typography className={classes.subtle}>
                Loading…
              </Typography>
            </div>
          )}
        </div>

        <Divider style={{ margin: '12px 0', background: 'rgba(255,255,255,0.12)' }} />

        <Typography className={classes.subtle}>
          Status: <span className={classes.kv}>{status}</span>
          {upload && upload.filename ? ` | file: ${upload.filename}` : ''}
          {upload && upload.flow_count != null ? ` | flows: ${upload.flow_count}` : ''}
        </Typography>

        {upload && upload.error_message ? (
          <Typography className={classes.error}>Server error_message: {String(upload.error_message)}</Typography>
        ) : null}

        {!!error && <Typography className={classes.error}>{error}</Typography>}
        {!!okMsg && <Typography className={classes.kv} style={{ marginTop: 10 }}>{okMsg}</Typography>}
      </Paper>

      <Dialog
        open={!!openStory}
        onClose={() => setOpenStory(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <span>PCAP Story</span>
          <IconButton onClick={() => setOpenStory(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography style={{ fontWeight: 900, marginBottom: 8 }}>
            {openStory && openStory.title ? String(openStory.title) : '(untitled)'}
          </Typography>
          <Typography style={{ opacity: 0.8, fontFamily: 'monospace', fontSize: 12, marginBottom: 10 }}>
            story_id: {openStory && openStory.story_id ? String(openStory.story_id) : ''}
            {' | '}created_at: {openStory && openStory.created_at ? String(openStory.created_at) : ''}
            {' | '}type: {openStory && openStory.story_type ? String(openStory.story_type) : ''}
            {' | '}severity: {openStory && openStory.severity ? String(openStory.severity) : ''}
            {' | '}status: {openStory && openStory.status ? String(openStory.status) : ''}
          </Typography>
          <Typography style={{ whiteSpace: 'pre-wrap' }}>
            {openStory && openStory.summary ? String(openStory.summary) : ''}
          </Typography>
          {openStory && openStory.error_message ? (
            <Typography style={{ marginTop: 10, color: '#b00020', whiteSpace: 'pre-wrap' }}>
              error_message: {String(openStory.error_message)}
            </Typography>
          ) : null}
          {(openStoryEvidence != null) ? (
            <pre style={{ marginTop: 14, fontSize: 12, whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(openStoryEvidence, null, 2)}
            </pre>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!openThesis}
        onClose={() => setOpenThesis(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <span>PCAP Thesis</span>
          <IconButton onClick={() => setOpenThesis(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography style={{ fontWeight: 900, marginBottom: 8 }}>
            {openThesis && openThesis.thesis ? String(openThesis.thesis) : '(no thesis text)'}
          </Typography>
          <Typography style={{ opacity: 0.8, fontFamily: 'monospace', fontSize: 12, marginBottom: 10 }}>
            thesis_id: {openThesis && openThesis.thesis_id ? String(openThesis.thesis_id) : ''}
            {' | '}created_at: {openThesis && openThesis.created_at ? String(openThesis.created_at) : ''}
            {' | '}horizon: {openThesis && openThesis.time_horizon ? String(openThesis.time_horizon) : ''}
            {' | '}confidence: {openThesis && openThesis.confidence != null ? String(openThesis.confidence) : ''}
            {' | '}status: {openThesis && openThesis.status ? String(openThesis.status) : ''}
          </Typography>

          {openThesis && openThesis.entry_conditions ? (
            <Typography style={{ marginTop: 10, whiteSpace: 'pre-wrap' }}>
              <span style={{ fontWeight: 900 }}>Entry conditions:</span> {String(openThesis.entry_conditions)}
            </Typography>
          ) : null}
          {openThesis && openThesis.invalidation_conditions ? (
            <Typography style={{ marginTop: 10, whiteSpace: 'pre-wrap' }}>
              <span style={{ fontWeight: 900 }}>Invalidation conditions:</span> {String(openThesis.invalidation_conditions)}
            </Typography>
          ) : null}
          {openThesis && openThesis.risks ? (
            <Typography style={{ marginTop: 10, whiteSpace: 'pre-wrap' }}>
              <span style={{ fontWeight: 900 }}>Risks:</span> {String(openThesis.risks)}
            </Typography>
          ) : null}
          {openThesis && openThesis.error_message ? (
            <Typography style={{ marginTop: 10, color: '#b00020', whiteSpace: 'pre-wrap' }}>
              error_message: {String(openThesis.error_message)}
            </Typography>
          ) : null}
          {openThesis && Array.isArray(openThesis.story_ids) && openThesis.story_ids.length ? (
            <Typography style={{ marginTop: 10, opacity: 0.8, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap' }}>
              story_ids: {openThesis.story_ids.map((x) => String(x)).join(', ')}
            </Typography>
          ) : null}
          {(openThesisEvidence != null) ? (
            <pre style={{ marginTop: 14, fontSize: 12, whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(openThesisEvidence, null, 2)}
            </pre>
          ) : null}
        </DialogContent>
      </Dialog>

      <Paper className={classes.card}>
        <Typography className={classes.title}>Stories</Typography>
        <Typography className={classes.subtle}>Rows: {Array.isArray(stories) ? stories.length : 0}</Typography>

        {(Array.isArray(stories) ? stories : []).map((s) => {
          const evidence = s && s.evidence_json ? safeJsonParse(String(s.evidence_json)) : null;
          return (
            <div key={(s && s.story_id) ? String(s.story_id) : Math.random()} className={classes.storyCard}>
              <div style={{ marginBottom: 6 }}>
                <span className={classes.chip}>{s && s.severity ? String(s.severity) : 'n/a'}</span>
                <span className={classes.chip}>{s && s.story_type ? String(s.story_type) : 'story'}</span>
                <span className={classes.subtle}>{s && s.created_at ? String(s.created_at) : ''}</span>
              </div>
              <Typography className={classes.storyTitle}>{s && s.title ? String(s.title) : '(untitled)'}</Typography>
              <Typography className={classes.subtle} style={{ marginTop: 4 }}>
                {s && s.summary ? String(s.summary) : ''}
              </Typography>
              <div style={{ marginTop: 10 }}>
                <Button variant="outlined" size="small" onClick={() => setOpenStory(s)}>
                  Details
                </Button>
              </div>
              {evidence ? (
                <Typography className={classes.kv} style={{ marginTop: 8 }}>
                  evidence: {JSON.stringify(evidence, null, 2)}
                </Typography>
              ) : null}
            </div>
          );
        })}

        {(!stories || !stories.length) && (
          <Typography className={classes.subtle} style={{ marginTop: 10 }}>
            No stories yet.
          </Typography>
        )}
      </Paper>

      <Paper className={classes.card}>
        <Typography className={classes.title}>Theses</Typography>
        <Typography className={classes.subtle}>Rows: {Array.isArray(theses) ? theses.length : 0}</Typography>

        {(Array.isArray(theses) ? theses : []).map((t) => {
          const evidence = t && t.evidence_json ? safeJsonParse(String(t.evidence_json)) : null;
          return (
            <div key={(t && t.thesis_id) ? String(t.thesis_id) : Math.random()} className={classes.storyCard}>
              <div style={{ marginBottom: 6 }}>
                <span className={classes.chip}>{t && t.time_horizon ? String(t.time_horizon) : 'horizon'}</span>
                <span className={classes.chip}>{t && t.confidence != null ? `confidence=${t.confidence}` : 'confidence=n/a'}</span>
                <span className={classes.subtle}>{t && t.created_at ? String(t.created_at) : ''}</span>
              </div>
              <Typography className={classes.storyTitle}>{t && t.thesis ? String(t.thesis) : '(no thesis text)'}</Typography>
              {t && t.entry_conditions ? (
                <Typography className={classes.subtle} style={{ marginTop: 6 }}>
                  Entry: {String(t.entry_conditions)}
                </Typography>
              ) : null}
              {t && t.invalidation_conditions ? (
                <Typography className={classes.subtle} style={{ marginTop: 6 }}>
                  Invalidation: {String(t.invalidation_conditions)}
                </Typography>
              ) : null}
              {t && t.risks ? (
                <Typography className={classes.subtle} style={{ marginTop: 6 }}>
                  Risks: {String(t.risks)}
                </Typography>
              ) : null}
              <div style={{ marginTop: 10 }}>
                <Button variant="outlined" size="small" onClick={() => setOpenThesis(t)}>
                  Details
                </Button>
              </div>
              {evidence ? (
                <Typography className={classes.kv} style={{ marginTop: 8 }}>
                  evidence: {JSON.stringify(evidence, null, 2)}
                </Typography>
              ) : null}
            </div>
          );
        })}

        {(!theses || !theses.length) && (
          <Typography className={classes.subtle} style={{ marginTop: 10 }}>
            No theses yet.
          </Typography>
        )}
      </Paper>
    </div>
  );
}
